/* global permutive */
/* eslint-disable camelcase */

import Logger from '../lib/logger';
import PluginBase from './base';
import Events, { EventTypes } from '../lib/events';
import ScriptLoader from '../lib/script_loader';
import { checkOneTrustCookieConsent, isMobileWebView } from '../lib/utils';

/** Slot keys we've already sent SlotViewable for (one event per slot) */
const slotViewableSent = new Set();
/** Slot keys we've already sent SlotClicked for (one event per slot) */
const slotClickedSent = new Set();
const VIEWABLE_THRESHOLD = 0.5;

const LOG_PREFIX = '[Permutive Slot Tracking]';

/**
 * Default Watson/Alchemy block merged under `page.article.watson` when `settings.permutive.watson`
 * is `true`. The `$alchemy_*` values are stored verbatim — ConcertAds does NOT resolve them; the
 * publisher supplies the real values (e.g. via a page data-layer that Permutive reads).
 * @type {Object}
 */
export const DEFAULT_ARTICLE_WATSON_DATA = {
  taxonomy: '$alchemy_taxonomy',
  keywords: '$alchemy_keywords',
  entities: '$alchemy_entities',
  concepts: '$alchemy_concepts',
};
export default class Permutive extends PluginBase {
  constructor({ app }) {
    super({ app });
    this.initialConsentCheckComplete = false;
    this._permutiveEnabledSlotsAttached = new Set();
    this.handleConsentUpdate = this.handleConsentUpdate.bind(this);
    this.handleSlotInserted = this.handleSlotInserted.bind(this);
  }

  onSettingsLoaded() {
    if (this.app.settings?.enablePermutiveSlotTracking === true) {
      Events.on(EventTypes.slotInserted, this.handleSlotInserted);
      Logger.log('Permutive: registered slotInserted listener for permutiveTracking.enabled slots');
    } else {
      Logger.log('Permutive: slot tracking disabled (enablePermutiveSlotTracking not true in settings)');
    }

    const eligible = this.isPermutiveSetupEligible();
    const requiresSetup = this.requiresPermutiveSetup();
    Logger.log(`Permutive onSettingsLoaded: eligible=${eligible}, requiresPermutiveSetup=${requiresSetup}`);

    if (eligible && requiresSetup) {
      this.listenForConsentUpdates();

      if (window.OneTrust && window.OnetrustActiveGroups) {
        const initialConsent = checkOneTrustCookieConsent();
        Logger.log(`Initial OneTrust consent status: ${initialConsent}`);
        this.handleConsentUpdate();
      } else {
        Logger.log('Permutive: OneTrust not present or OnetrustActiveGroups not ready, skipping initial consent check');
      }
    } else {
      if (!eligible) Logger.log('Permutive: not eligible (missing projectId/publicApiKey), skipping consent flow');
      if (eligible && !requiresSetup) Logger.log('Permutive: classifier-only mode, skipping full setup');
    }
  }

  /**
   * When slotInserted fires: treat as enabled if payload.enablePermutiveSlotTracking is true, or if the
   * slot name matches a configured slot (including _1, _2 suffix) that has permutiveTracking.enabled.
   * If the config has permutiveTracking.containerSelector we attach to that container; otherwise to the slot element.
   */
  handleSlotInserted(_eventName, { payload }) {
    const slotName = payload?.slotName;
    const slot = this.app.slots.get(slotName);
    const ptFromSlot = slot?.data?.permutiveTracking;
    const baseConfig = getSlotConfigForPermutive(this.app, slotName);
    const ptFromConfig = baseConfig?.permutiveTracking;
    // Prefer slot.data; fall back to settings.slots for the same YAML entry. This makes things easier for testing.
    const pt = ptFromSlot || ptFromConfig;
    const enabled = payload?.enablePermutiveSlotTracking === true || pt?.enabled === true;
    if (!enabled) {
      return;
    }

    this.attachTrackingForSlot(slotName, {
      containerSelector: pt?.containerSelector,
      linkSelector: pt?.linkSelector || 'a',
    });
  }

  /**
   * Attach SlotViewable (IntersectionObserver 50%) and SlotClicked for a Permutive-enabled slot.
   * When containerSelector is set, observes the container (e.g. Duet card) and attaches click to the link
   * inside it; otherwise observes and attaches click to the slot element itself.
   */
  attachTrackingForSlot(slotName, options = {}) {
    const { containerSelector, linkSelector = 'a' } = options;
    if (!slotName) return;
    if (!this.isPermutiveSetupEligible()) {
      Logger.log(`${LOG_PREFIX} attachTrackingForSlot: not eligible, skipping`);
      return;
    }

    const slot = this.app.slots.get(slotName);
    if (!slot || typeof slot.getElement !== 'function') {
      Logger.log(`${LOG_PREFIX} slot not found or no getElement for ${slotName}`);
      return;
    }

    const el = slot.getElement();
    if (!el) {
      Logger.log(`${LOG_PREFIX} no element for slot=${slotName}`);
      return;
    }

    const viewabilityTarget = containerSelector ? (el.closest && el.closest(containerSelector)) || el : el;
    const attachmentKey = slot.id || slotName;
    if (this._permutiveEnabledSlotsAttached.has(attachmentKey)) {
      Logger.log(`${LOG_PREFIX} already attached for slot=${attachmentKey}`);
      return;
    }
    this._permutiveEnabledSlotsAttached.add(attachmentKey);

    const isContainer = !!containerSelector && viewabilityTarget !== el;
    if (containerSelector && viewabilityTarget === el) {
      Logger.log(`${LOG_PREFIX} attach: containerSelector did not match (slot element used), slot=${slotName}`);
    }
    const slotType = slot.constructor?.name || typeof slot;
    Logger.log(
      `${LOG_PREFIX} attach: slot=${slotName} ${isContainer ? '(container)' : '(direct)'} slotType=${slotType} ` +
        (containerSelector ? `containerSelector=${containerSelector} linkSelector=${linkSelector} ` : '') +
        `hasGamAtAttach=${!!slot._permutiveGamResponse}`
    );

    if (containerSelector && viewabilityTarget !== el && !viewabilityTarget.id && viewabilityTarget.setAttribute) {
      viewabilityTarget.setAttribute(
        'id',
        `permutive-slot-${Date.now()}-${Math.random()
          .toString(36)
          .slice(2, 9)}`
      );
    }

    // buildPermutiveSlotPayload only uses slotName, width, height, advertiser_id, campaign_id, line_item_id, creative_id
    const buildPayload = (opts = {}) => {
      const payloadOpts = { slotName, ...slot._permutiveGamResponse, ...opts };
      return withPermutiveClient(this.app, buildPermutiveSlotPayload(this.app, viewabilityTarget, payloadOpts));
    };

    attachPermutiveViewabilityObserver({ viewabilityTarget, slotName, buildPayload });
    attachPermutiveSlotClickListener({
      viewabilityTarget,
      containerSelector,
      linkSelector,
      slotName,
      buildPayload,
    });

    Logger.log(`${LOG_PREFIX} attached SlotViewable/SlotClicked for slot=${slotName}`);
  }

  handleConsentUpdate() {
    const consentGranted = checkOneTrustCookieConsent();
    Logger.log(`Handling consent update: ${consentGranted}`);

    if (consentGranted) {
      this.initializePermutive();
      this.initialConsentCheckComplete = true;
    } else {
      this.clearPermutiveData();
    }
  }

  async initializePermutive() {
    try {
      Logger.log('Initializing Permutive after consent granted');

      const permutiveWasCleared = !window.localStorage.getItem('permutive-id');

      if (!window.permutive) {
        addPermutiveProvidedCode(this.app.settings.permutive.projectId, this.app.settings.permutive.publicApiKey);
      }

      permutive.consent({ opt_in: true, token: 'CONSENT_GRANTED' });

      if (!this.initialConsentCheckComplete || permutiveWasCleared) {
        if (!this.onlySyncClassifiers()) {
          await this.loadClassifierDependencies();
        }
        await this.setupPermutiveTargeting();
      }
    } catch (error) {
      Logger.error(`Failed to initialize Permutive: ${error}`);
    }
  }

  /**
   * Do required configs exist to set up Permutive, and is Permutive enabled for this site?
   * @return {Boolean}
   */
  isPermutiveSetupEligible() {
    return (
      !!this.app.settings.permutiveEnabled &&
      !!this.app.settings.permutive?.projectId &&
      !!this.app.settings.permutive?.publicApiKey
    );
  }

  /**
   * Does this site need Permutive to be set up? Vox O&O and SaaS sites that have Permutive enabled need the
   * Permutive script and code provide by Permutive added. Tracking data including classifiers is also sent with page data.
   * @return {Boolean}
   */
  requiresPermutiveSetup() {
    return this.app.settings.permutiveEnabled && !this.onlySyncClassifiers();
  }

  /**
   * Does this site only need classifiers sent to Permutive? NYM sets up Permutive so only needs ConcertAds to
   * send classifier data as custom events. This is temporary, NYM will be updated so classifier data is sent
   * along with other page data.
   * @return {Boolean}
   */
  onlySyncClassifiers() {
    return !!this.app.settings.permutiveClassifiersEnabled;
  }

  /**
   * Is the Navi Widget enabled on this network?
   * @return {Boolean}
   */
  syncingWithNaviWidgetEnabled() {
    return !!this.app.settings.permutiveSyncingWithNaviWidgetEnabled;
  }

  /**
   * Listen for OneTrust consent updates
   */
  listenForConsentUpdates() {
    window.removeEventListener('OneTrustGroupsUpdated', this.handleConsentUpdate);
    window.addEventListener('OneTrustGroupsUpdated', this.handleConsentUpdate);
  }

  /**
   * Clear all Permutive data and targeting when consent is withdrawn
   * @returns {undefined}
   */
  clearPermutiveData() {
    try {
      Logger.log('Clearing Permutive data and targeting');

      // Set consent to false if Permutive exists
      if (window.permutive) {
        permutive.consent({ opt_in: false });
      }

      // Clear Permutive segments from localStorage
      try {
        window.localStorage.removeItem('_pdfps');
        window.localStorage.removeItem('permutive-id');

        // Clear any other Permutive-specific localStorage items
        Object.keys(window.localStorage).forEach(key => {
          if (key.startsWith('_perm_') || key.startsWith('permutive')) {
            window.localStorage.removeItem(key);
          }
        });
      } catch (storageError) {
        Logger.error(`Error clearing Permutive localStorage: ${storageError}`);
      }

      // Clear Permutive cookies
      try {
        const cookies = document.cookie.split(';');
        cookies.forEach(cookie => {
          const cookieName = cookie.split('=')[0].trim();
          if (cookieName.toLowerCase().includes('permutive')) {
            document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=.${
              window.location.hostname
            }`;
          }
        });
      } catch (cookieError) {
        Logger.error(`Error clearing Permutive cookies: ${cookieError}`);
      }

      // Clear targeting variables
      try {
        this.app.addVariable('permutive', []);
      } catch (variableError) {
        Logger.error(`Error clearing Permutive targeting variables: ${variableError}`);
      }

      // Remove any existing Permutive events listeners
      try {
        Events.off(EventTypes.allClassifierValuesAdded);
      } catch (eventError) {
        Logger.error(`Error removing Permutive event listeners: ${eventError}`);
      }

      Logger.log('Permutive data, cookies, and targeting cleared');
    } catch (error) {
      Logger.error(`Failed to clear Permutive data: ${error}`);
    }
  }

  /**
   * Set up Permutive targeting after consent is granted
   * @returns {Promise<void>}
   */
  async setupPermutiveTargeting() {
    Logger.log('Setting up Permutive targeting after consent granted');
    this.addPermutiveTargeting();

    if (this.onlySyncClassifiers()) {
      Events.on(EventTypes.allClassifierValuesAdded, () => {
        this.syncClassifiers();
        this.addPermutiveTargeting();
      });
    } else {
      Events.on(EventTypes.allClassifierValuesAdded, () => {
        this.loadClassifierDependencies();
      });
    }

    if (this.syncingWithNaviWidgetEnabled()) {
      this.syncNaviWidgetValues();
    }
  }

  /**
   * Setup Permutive code and tracking
   * @return {undefined}
   */
  async loadClassifierDependencies() {
    // When waitForPageMetaData is enabled, hold the page-data send until the page supplies its
    // metadata; resolves immediately when the flag is off, leaving behavior unchanged. Read the
    // metadata via get() AFTER the gate resolves so a later setPageMetaData (SPA nav / reinstall)
    // is reflected instead of a stale first snapshot; when disabled, fall back to the variables path.
    await this.app.pageMetaData.whenReady();
    const meta = this.app.pageMetaData.enabled ? this.app.pageMetaData.get() : {};
    this.addPageDataToPermutive(meta);
    await this.setPermutiveIdentity();
    await this.loadPermutiveScript();
  }

  /**
   * Send page data to Permutive. When `meta` (from setPageMetaData) has page/article, it is the
   * source of truth; otherwise the data is derived from app.variables. See {@link getPageData}.
   *
   * When `settings.permutive.watson === true`, the built-in {@link DEFAULT_ARTICLE_WATSON_DATA} block is
   * merged under `page.article.watson`, regardless of which path produced the page data. A
   * publisher-supplied `page.article.watson` (via setPageMetaData) wins over it. (Custom watson
   * objects are a planned future enhancement — only the boolean opt-in is supported for now.)
   * @param {{ page?: object, article?: object }} [meta]
   * @return {undefined}
   */
  addPageDataToPermutive(meta = {}) {
    const pageData = getPageData(this.app.variables, meta);

    if (this.app.settings.permutive?.watson === true) {
      pageData.page.article = {
        ...pageData.page.article,
        watson: { ...DEFAULT_ARTICLE_WATSON_DATA, ...(pageData.page.article?.watson || {}) },
      };
    }

    permutive.addon('web', withPermutiveClient(this.app, pageData));
  }

  /**
   * Will use the permutive publisher provided user id
   * instead of trying to fingerprint this user
   * @returns {undefined}
   */
  async setPermutiveIdentity() {
    const permutiveIdentity = await this.app.identityFor('permutive');
    if (permutiveIdentity) {
      permutive.identify([
        {
          id: permutiveIdentity,
          tag: 'publisherUserId',
        },
      ]);
    } else {
      Logger.log(`Unable to set publisher id for permutive`);
    }
  }

  /**
   * Set Permutive targeting
   * @return {undefined}
   */
  addPermutiveTargeting() {
    const pdfps = window.localStorage.getItem('_pdfps');
    this.app.addVariable('permutive', pdfps ? JSON.parse(pdfps) : []);
  }

  /**
   * Load Permutive script
   * @return {undefined}
   */
  async loadPermutiveScript() {
    const dependency = {
      url: `https://cdn.permutive.app/${this.app.settings.permutive.projectId}-web.js`,
      timeout: this.app.settings.permutive?.scriptTimeout || 3000,
    };

    try {
      const url = await new ScriptLoader().load(dependency);
      Logger.log(`Permutive script ${url} has loaded successfully`);
    } catch (error) {
      Logger.error(`Error loading Permutive script: ${error}`);
    }
  }

  /**
   * Sync classifiers with Permutive
   * @return {undefined}
   */
  syncClassifiers() {
    const { cts_keyword, cts_keyword_list, cts_iab_category, c_sg } = this.app.variables;

    if (cts_keyword) this.syncMotifTargetingWithPermutive('CtsKeyword', cts_keyword);
    if (cts_keyword_list) this.syncMotifTargetingWithPermutive('CtsKeywordList', cts_keyword_list);
    if (cts_iab_category) this.syncMotifTargetingWithPermutive('CtsIabCategory', cts_iab_category);
    if (c_sg) this.syncContextualSegmentsWithPermutive(c_sg);
  }

  /**
   * Sync Motif Targeting with Permutive
   * @param {String} key
   * @param {String} value
   * @return {Object}
   */
  syncMotifTargetingWithPermutive(key, value) {
    Logger.log(`Syncing Motif targeting to Permutive ${value}`);
    try {
      permutive.track(key, withPermutiveClient(this.app, { value: value }));
    } catch (e) {
      Logger.log(`Unable to track permutive event, received ${e.name} - ${e.message}`);
    }
  }

  /**
   * Sync Contextual Segmants with Permutive
   * @param {Array} contextualSegments
   * @return {Object}
   */
  syncContextualSegmentsWithPermutive(contextualSegments) {
    contextualSegments.forEach(segment => {
      Logger.log(`Syncing contextual segments to Permutive ${segment}`);
      try {
        permutive.track('ContextClassified', withPermutiveClient(this.app, { id: segment, type: 'Interest' }));
      } catch (e) {
        Logger.log(`Unable to track permutive event, received ${e.name} - ${e.message}`);
      }
    });
  }

  /**
   * Listen for a post message from a Navi Widget creative
   * If a message is sent with the event data, track it with Permutive
   */
  syncNaviWidgetValues() {
    window.addEventListener(
      'message',
      event => {
        if (event?.data?.indexOf && event.data.indexOf('NAVI WIDGET') > 0) {
          try {
            var naviData = JSON.parse(event.data);

            if (Array.isArray(naviData)) {
              permutive.track(
                'AdInteraction',
                withPermutiveClient(this.app, {
                  tradeInPhone: naviData[1],
                  selectedPhone: naviData[2],
                  carrier: naviData[3],
                })
              );
            }
          } catch (e) {
            Logger.log(`Error tracking Permutive event for Navi Widget data, received ${e.name} - ${e.message}`);
          }
        }
      },
      false
    );
  }
}

/**
 * Build Permutive SlotViewable / SlotClicked payload. Uses app.settings.slug for ad_unit_path and
 * options.slotName (e.g. native_ad_fan_post_1), then container id, then generated id for slot_element_id.
 * Permutive schema also allows optional advertiser_id, campaign_id, line_item_id, creative_id
 * (for technographic/demographic and wrap reports); pass them in options when available (e.g. from GAM).
 * @param {Object} app - ConcertAds app instance
 * @param {Element} container - DOM container for the ad
 * @param {Object} [options] - Optional: slotName, width, height; optional schema: advertiser_id, campaign_id, line_item_id, creative_id
 * Note: is_empty is intentionally excluded — Permutive's schema does not allow additional properties and rejects it at runtime.
 * @returns {Object} Payload matching Permutive schema
 */
export function buildPermutiveSlotPayload(app, container, options = {}) {
  const adUnitPath = app.settings?.slug || '';
  const slotElementId =
    options.slotName ||
    (container && container.id) ||
    (container && container.getAttribute && container.getAttribute('id')) ||
    `permutive-slot-${Date.now()}-${Math.random()
      .toString(36)
      .slice(2, 9)}`;

  const payload = {
    service_name: 'concert',
    slot: {
      ad_unit_path: adUnitPath,
      slot_element_id: slotElementId,
    },
  };

  if (options.width != null) payload.width = options.width;
  if (options.height != null) payload.height = options.height;
  if (options.advertiser_id != null) payload.advertiser_id = options.advertiser_id;
  if (options.campaign_id != null) payload.campaign_id = options.campaign_id;
  if (options.line_item_id != null) payload.line_item_id = options.line_item_id;
  if (options.creative_id != null) payload.creative_id = options.creative_id;

  return payload;
}

/**
 * Get page data to send to Permutive.
 *
 * When `meta` (supplied via setPageMetaData, only when waitForPageMetaData is enabled) has a
 * `page` or `article`, it is the source of truth: all of its `page` properties are copied and the
 * supplied `article` is nested under them — everything variable-derived (including browser_language,
 * contextClassified and cts) is intentionally dropped (full override). Otherwise the data is
 * derived from variables (classifier data included) as before.
 *
 * @param {Object} variables
 * @param {{ page?: object, article?: object }} [meta]
 * @return {Object}
 */
export function getPageData(variables, meta = {}) {
  if (meta.page || meta.article) {
    return { page: { ...meta.page, article: { ...meta.article } } };
  }

  return {
    page: {
      type: variables.page_type?.[0] || '',
      article: {
        id: variables.entry_id?.[0] || '',
        title: variables.entry_title?.[0] || '',
        description: variables.entry_blurb?.[0] || '',
        authors: variables.entry_author || [''],
        section: variables.entry_group?.[0] || '',
        publishedAt: variables.entry_published_date?.[0] || new Date().toISOString(),
        keywords: typeof variables.keywords === 'string' ? variables.keywords.split(' ') : variables.keywords,
      },
      browser_language: window.navigator.language || '',
      contextClassified: {
        type: 'Interest',
        id: variables.c_sg || [],
      },
      cts: {
        keyword: variables.cts_keyword || [],
        keywordList: variables.cts_keyword_list || [],
        iabCategory: variables.cts_iab_category || [],
      },
    },
  };
}

/**
 * Detect an in-app WebView request. Duet sets the `in_app` dfpVariable for GAM targeting; we reuse
 * it here ONLY as the gate for attaching the iOS client context to Permutive — its value is never
 * placed in a Permutive payload. Delegates to shared isMobileWebView normalization.
 * @param {Object} variables - app.variables
 * @return {Boolean}
 */
export function isAppWebView(variables) {
  return isMobileWebView(variables);
}

/**
 * Permutive "All Events" client context, matching the live Eater app (client.type / client.user_agent).
 * Eater's native SDK sends { type: 'iOS', user_agent: '<osName> <osVersion>; <model>' }. On the web
 * SDK we can only supply the WKWebView UA for user_agent (a best-effort stand-in for Eater's
 * expo-device descriptor). `type` is 'iOS' for this pass; Android app support is a follow-up.
 * Cached for the life of the page since it does not change.
 * @return {{type: string, user_agent: string}}
 */
let cachedPermutiveClient;
export function buildPermutiveClient() {
  if (!cachedPermutiveClient) {
    cachedPermutiveClient = {
      type: 'iOS',
      user_agent: (window.navigator && window.navigator.userAgent) || '',
    };
  }
  return cachedPermutiveClient;
}

/**
 * Merge the iOS client context into a set of Permutive event/page properties, but only for in-app
 * WebView requests. Caller-supplied `client` fields win, so an event can add extra client.* keys
 * without losing type/user_agent. For non-app (normal web) requests the properties are returned
 * unchanged so the web SDK's default client is preserved.
 * @param {Object} app - ConcertAds app instance
 * @param {Object} [properties] - Permutive event/page properties
 * @return {Object}
 */
export function withPermutiveClient(app, properties = {}) {
  if (!isAppWebView(app && app.variables)) return properties;
  const existingClient =
    properties.client && typeof properties.client === 'object' && !Array.isArray(properties.client)
      ? properties.client
      : {};
  return { ...properties, client: { ...buildPermutiveClient(), ...existingClient } };
}

/**
 * Add code provided by Permutive
 * @param {String} projectId      Permutive Project ID
 * @param {String} publicApiKey   Permutive Public API key
 * @return {undefined}
 */
function addPermutiveProvidedCode(projectId, publicApiKey) {
  !(function(n, e, o, r, i) {
    if (!e) {
      (e = e || {}),
        (window.permutive = e),
        (e.q = []),
        (e.config = i || {}),
        (e.config.projectId = o),
        (e.config.apiKey = r),
        (e.config.environment = e.config.environment || 'production');
      for (
        var t = [
            'addon',
            'identify',
            'track',
            'trigger',
            'query',
            'segment',
            'segments',
            'ready',
            'on',
            'once',
            'user',
            'consent',
          ],
          c = 0;
        c < t.length;
        c++
      ) {
        var f = t[c];
        e[f] = (function(n) {
          return function() {
            var o = Array.prototype.slice.call(arguments, 0);
            e.q.push({ functionName: n, arguments: o });
          };
        })(f);
      }
    }
  })(document, window.permutive, projectId, publicApiKey, {});
}

function logGamPayloadSummary(prefix, eventLabel, payload) {
  const has =
    payload.advertiser_id != null ||
    payload.campaign_id != null ||
    payload.line_item_id != null ||
    payload.creative_id != null;
  Logger.log(
    `${prefix} ${eventLabel} slot_element_id=${payload.slot?.slot_element_id || '?'} hasGamIds=${has} ` +
      `advertiser_id=${payload.advertiser_id ?? 'n/a'} campaign_id=${payload.campaign_id ?? 'n/a'} ` +
      `line_item_id=${payload.line_item_id ?? 'n/a'} creative_id=${payload.creative_id ?? 'n/a'}`
  );
}

/**
 * IntersectionObserver: when viewability target is ≥50% visible, send SlotViewable once and disconnect.
 */
function attachPermutiveViewabilityObserver({ viewabilityTarget, slotName, buildPayload }) {
  const observer = new IntersectionObserver(
    entries => {
      entries.forEach(entry => {
        if (entry.intersectionRatio >= VIEWABLE_THRESHOLD) {
          observer.disconnect();
          if (slotViewableSent.has(slotName)) return;
          slotViewableSent.add(slotName);
          Logger.log(`${LOG_PREFIX} viewable 50% reached for slot=${slotName}, sending SlotViewable`);
          const payload = buildPayload();
          logGamPayloadSummary(LOG_PREFIX, 'SlotViewable sent', payload);
          if (window.permutive && typeof window.permutive.track === 'function') {
            try {
              window.permutive.track('SlotViewable', payload);
            } catch (e) {
              Logger.log(`${LOG_PREFIX} SlotViewable error: ${e.name} - ${e.message}`);
            }
          }
        }
      });
    },
    { threshold: VIEWABLE_THRESHOLD, root: null }
  );
  observer.observe(viewabilityTarget);
}

/**
 * Element that receives the click: direct slot = viewability target; container mode = link inside it.
 */
function resolvePermutiveClickTarget(viewabilityTarget, containerSelector, linkSelector) {
  if (!containerSelector) return viewabilityTarget;
  return viewabilityTarget.querySelector && viewabilityTarget.querySelector(linkSelector);
}

/**
 * One SlotClicked per slot; uses viewability target for width/height on click.
 */
function attachPermutiveSlotClickListener({
  viewabilityTarget,
  containerSelector,
  linkSelector,
  slotName,
  buildPayload,
}) {
  const clickTarget = resolvePermutiveClickTarget(viewabilityTarget, containerSelector, linkSelector);
  if (clickTarget) {
    clickTarget.addEventListener('click', () => {
      if (slotClickedSent.has(slotName)) return;
      slotClickedSent.add(slotName);
      const rect = viewabilityTarget.getBoundingClientRect && viewabilityTarget.getBoundingClientRect();
      const width = rect ? Math.round(rect.width) : undefined;
      const height = rect ? Math.round(rect.height) : undefined;
      const payload = buildPayload({ width, height });
      logGamPayloadSummary(LOG_PREFIX, 'SlotClicked sent', payload);
      if (window.permutive && typeof window.permutive.track === 'function') {
        try {
          window.permutive.track('SlotClicked', payload);
        } catch (e) {
          Logger.log(`${LOG_PREFIX} SlotClicked error: ${e.name} - ${e.message}`);
        }
      }
    });
  } else if (containerSelector) {
    Logger.log(`${LOG_PREFIX} no link in container (${linkSelector}), SlotClicked will not fire`);
  }
}

/**
 * Find the slot config that matches this slot name. Slot names may be the configured name
 * (e.g. native_ad_fan_post) or that name with a _N suffix for uniqueness (e.g. native_ad_fan_post_1).
 * @param {Object} app - ConcertAds app
 * @param {string} slotName - Slot instance name (e.g. native_ad_fan_post_1)
 * @returns {Object|undefined} Slot config from settings.slots, or undefined
 */
export function getSlotConfigForPermutive(app, slotName) {
  const slots = app.settings?.slots;
  if (!Array.isArray(slots)) return undefined;
  const exact = slots.find(c => c.name === slotName);
  if (exact) return exact;
  return slots.find(config => {
    const base = config.name;
    if (!base || typeof slotName !== 'string' || !slotName.startsWith(base)) return false;
    const suffix = slotName.slice(base.length);
    return suffix.charAt(0) === '_' && /^\d+$/.test(suffix.slice(1));
  });
}
